// Gráfico de linha "Esperados vs Presentes" ao longo dos dias letivos do mês
// (ver GET /api/relatorios/estatisticas-mensais). Construído à mão em SVG —
// não há biblioteca de gráficos no projeto e o volume de dados (no máximo ~26
// pontos/mês) não justifica adicionar uma. Duas séries na MESMA escala (ambas
// são contagem de alunos), então cabem no mesmo eixo sem gambiarra de eixo duplo.
//
// "Esperados" é calculado a partir da matrícula ATUAL aplicada retroativamente
// a cada dia do mês — como matrículas mudam com frequência (turma trocada,
// aluno movido), um dia específico pode legitimamente mostrar mais "presentes"
// do que "esperados" hoje. Isso é mostrado como está, sem esconder: o gráfico
// não força uma % nem um teto de 100%, deixa a diferença visível pro leitor.
import React, { useState, useRef, useLayoutEffect, useMemo, useCallback } from 'react';

const ALTURA = 240;
const MARGEM = { topo: 16, direita: 12, baixo: 28, esquerda: 34 };

function arredondarTopo(valorMax) {
  if (valorMax <= 0) return 10;
  const passo = Math.pow(10, Math.floor(Math.log10(valorMax)));
  const normalizado = valorMax / passo;
  let passoLimpo;
  if (normalizado <= 1) passoLimpo = 1;
  else if (normalizado <= 2) passoLimpo = 2;
  else if (normalizado <= 5) passoLimpo = 5;
  else passoLimpo = 10;
  return Math.ceil(valorMax / (passoLimpo * passo)) * passoLimpo * passo;
}

function formatarDataCurta(iso) {
  const [, mes, dia] = iso.split('-');
  return `${dia}/${mes}`;
}

// Curva suave (Catmull-Rom convertido pra Bézier cúbica) em vez de segmentos
// retos ligando ponto a ponto — só estética, os pontos (e os valores lidos no
// hover/tooltip) continuam exatamente os mesmos.
function caminhoSuave(pontos) {
  if (pontos.length === 0) return '';
  if (pontos.length === 1) return `M ${pontos[0].x} ${pontos[0].y}`;
  let d = `M ${pontos[0].x} ${pontos[0].y}`;
  for (let i = 0; i < pontos.length - 1; i++) {
    const p0 = pontos[i === 0 ? i : i - 1];
    const p1 = pontos[i];
    const p2 = pontos[i + 1];
    const p3 = pontos[i + 2 < pontos.length ? i + 2 : i + 1];
    const cp1x = p1.x + (p2.x - p0.x) / 6;
    const cp1y = p1.y + (p2.y - p0.y) / 6;
    const cp2x = p2.x - (p3.x - p1.x) / 6;
    const cp2y = p2.y - (p3.y - p1.y) / 6;
    d += ` C ${cp1x.toFixed(2)} ${cp1y.toFixed(2)}, ${cp2x.toFixed(2)} ${cp2y.toFixed(2)}, ${p2.x.toFixed(2)} ${p2.y.toFixed(2)}`;
  }
  return d;
}

function MonthlyTrendChart({ dados }) {
  const containerRef = useRef(null);
  const [largura, setLargura] = useState(600);
  const [hoverIdx, setHoverIdx] = useState(null);

  useLayoutEffect(() => {
    if (!containerRef.current) return;
    const observer = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect?.width;
      if (w) setLargura(w);
    });
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  const larguraUtil = Math.max(0, largura - MARGEM.esquerda - MARGEM.direita);
  const alturaUtil = ALTURA - MARGEM.topo - MARGEM.baixo;

  const maxValor = useMemo(() => {
    const maiorValor = dados.reduce((max, d) => Math.max(max, d.esperados, d.presentes), 0);
    return arredondarTopo(maiorValor || 1);
  }, [dados]);

  const x = useCallback(
    (i) => {
      if (dados.length <= 1) return MARGEM.esquerda;
      return MARGEM.esquerda + (i / (dados.length - 1)) * larguraUtil;
    },
    [dados.length, larguraUtil],
  );

  const y = useCallback(
    (valor) => {
      return MARGEM.topo + alturaUtil - (valor / maxValor) * alturaUtil;
    },
    [alturaUtil, maxValor],
  );

  const pontosPresentes = useMemo(
    () => dados.map((d, i) => ({ x: x(i), y: y(d.presentes) })),
    [dados, x, y],
  );
  const pontosEsperados = useMemo(
    () => dados.map((d, i) => ({ x: x(i), y: y(d.esperados) })),
    [dados, x, y],
  );

  const linhaPresentes = useMemo(() => caminhoSuave(pontosPresentes), [pontosPresentes]);
  const linhaEsperados = useMemo(() => caminhoSuave(pontosEsperados), [pontosEsperados]);

  // Área com gradiente por baixo da linha "Presentes" — fecha o caminho suave
  // no eixo de base (y do valor 0) pra virar um polígono preenchível.
  const areaPresentes = useMemo(() => {
    if (pontosPresentes.length === 0) return '';
    const base = y(0);
    const ultimo = pontosPresentes[pontosPresentes.length - 1];
    const primeiro = pontosPresentes[0];
    return `${linhaPresentes} L ${ultimo.x.toFixed(2)} ${base} L ${primeiro.x.toFixed(2)} ${base} Z`;
  }, [linhaPresentes, pontosPresentes, y]);

  const ticksY = useMemo(() => {
    const n = 4;
    return Array.from({ length: n + 1 }, (_, i) => Math.round((maxValor / n) * i));
  }, [maxValor]);

  // Só rotula alguns dias no eixo X (a cada ~5) — rotular todos vira ruído ilegível.
  const passoRotuloX = Math.max(1, Math.ceil(dados.length / 7));

  const moverPonteiro = (evento) => {
    if (dados.length === 0) return;
    const rect = containerRef.current.getBoundingClientRect();
    const posX = evento.clientX - rect.left;
    let melhorIdx = 0;
    let melhorDist = Infinity;
    dados.forEach((_, i) => {
      const dist = Math.abs(x(i) - posX);
      if (dist < melhorDist) {
        melhorDist = dist;
        melhorIdx = i;
      }
    });
    setHoverIdx(melhorIdx);
  };

  if (dados.length === 0) {
    return (
      <p className="text-sm text-gray-400 dark:text-gray-500 text-center py-12">
        Nenhum dia letivo nesse mês ainda.
      </p>
    );
  }

  const hover = hoverIdx !== null ? dados[hoverIdx] : null;

  return (
    <div>
      <div className="flex items-center gap-4 mb-2">
        <div className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
          <span
            className="w-3 h-0.5 rounded-full bg-gray-400 dark:bg-gray-600"
            style={{
              backgroundImage:
                'repeating-linear-gradient(to right, currentColor 0, currentColor 3px, transparent 3px, transparent 5px)',
            }}
          />
          Esperados
        </div>
        <div className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
          <span className="w-3 h-0.5 rounded-full bg-gradient-to-r from-blue-500 to-cyan-400" />
          Presentes
        </div>
      </div>

      <div ref={containerRef} className="relative">
        <svg
          width="100%"
          height={ALTURA}
          onPointerMove={moverPonteiro}
          onPointerLeave={() => setHoverIdx(null)}
          className="overflow-visible"
        >
          <defs>
            <linearGradient id="areaPresentesGrad" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.28" />
              <stop offset="100%" stopColor="#3b82f6" stopOpacity="0" />
            </linearGradient>
            <linearGradient id="linhaPresentesGrad" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#3b82f6" />
              <stop offset="100%" stopColor="#22d3ee" />
            </linearGradient>
            <filter id="pontoGlow" x="-100%" y="-100%" width="300%" height="300%">
              <feGaussianBlur stdDeviation="3" result="blur" />
              <feMerge>
                <feMergeNode in="blur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>

          {ticksY.map((val) => (
            <g key={val}>
              <line
                x1={MARGEM.esquerda}
                x2={MARGEM.esquerda + larguraUtil}
                y1={y(val)}
                y2={y(val)}
                className="stroke-gray-100 dark:stroke-gray-800"
                strokeWidth={1}
                strokeDasharray="3 4"
                vectorEffect="non-scaling-stroke"
              />
              <text
                x={MARGEM.esquerda - 8}
                y={y(val)}
                textAnchor="end"
                dominantBaseline="middle"
                className="fill-gray-400 dark:fill-gray-500 text-[10px]"
              >
                {val}
              </text>
            </g>
          ))}

          {dados.map(
            (d, i) =>
              i % passoRotuloX === 0 && (
                <text
                  key={d.data}
                  x={x(i)}
                  y={ALTURA - 8}
                  textAnchor="middle"
                  className="fill-gray-400 dark:fill-gray-500 text-[10px]"
                >
                  {formatarDataCurta(d.data)}
                </text>
              ),
          )}

          <path d={areaPresentes} fill="url(#areaPresentesGrad)" stroke="none" />

          <path
            d={linhaEsperados}
            fill="none"
            className="stroke-gray-400 dark:stroke-gray-600"
            strokeWidth={2}
            strokeDasharray="5 5"
            strokeLinecap="round"
            strokeLinejoin="round"
            vectorEffect="non-scaling-stroke"
          />
          <path
            d={linhaPresentes}
            fill="none"
            stroke="url(#linhaPresentesGrad)"
            strokeWidth={2.5}
            strokeLinecap="round"
            strokeLinejoin="round"
            vectorEffect="non-scaling-stroke"
            filter="url(#pontoGlow)"
          />

          {hover && (
            <>
              <line
                x1={x(hoverIdx)}
                x2={x(hoverIdx)}
                y1={MARGEM.topo}
                y2={MARGEM.topo + alturaUtil}
                className="stroke-gray-300 dark:stroke-gray-700"
                strokeWidth={1}
                strokeDasharray="3 3"
                vectorEffect="non-scaling-stroke"
              />
              <circle
                cx={x(hoverIdx)}
                cy={y(hover.esperados)}
                r={4}
                className="fill-gray-400 dark:fill-gray-600 stroke-white dark:stroke-gray-900"
                strokeWidth={2}
              />
              <circle
                cx={x(hoverIdx)}
                cy={y(hover.presentes)}
                r={5}
                fill="#3b82f6"
                stroke="white"
                strokeWidth={2}
                filter="url(#pontoGlow)"
              />
            </>
          )}
        </svg>

        {hover && (
          <div
            className="absolute top-2 pointer-events-none bg-white/95 dark:bg-gray-900/95 backdrop-blur-sm border border-gray-100 dark:border-gray-800 rounded-2xl shadow-xl px-3 py-2 text-xs min-w-[130px] overflow-hidden"
            style={{
              left: Math.min(Math.max(x(hoverIdx), 70), largura - 70),
              transform: 'translateX(-50%)',
            }}
          >
            <span className="absolute inset-x-0 top-0 h-0.5 bg-gradient-to-r from-blue-500 to-cyan-400" />
            <p className="font-medium text-gray-900 dark:text-gray-100 mb-1.5">
              {formatarDataCurta(hover.data)}
            </p>
            <div className="flex items-center justify-between gap-3">
              <span className="text-gray-500 dark:text-gray-400 flex items-center gap-1.5">
                <span className="w-2.5 h-0.5 bg-gray-400 dark:bg-gray-600 inline-block rounded-full" />
                Esperados
              </span>
              <span className="font-semibold text-gray-900 dark:text-gray-100">
                {hover.esperados}
              </span>
            </div>
            <div className="flex items-center justify-between gap-3 mt-0.5">
              <span className="text-gray-500 dark:text-gray-400 flex items-center gap-1.5">
                <span className="w-2.5 h-0.5 bg-gradient-to-r from-blue-500 to-cyan-400 inline-block rounded-full" />
                Presentes
              </span>
              <span className="font-semibold text-gray-900 dark:text-gray-100">
                {hover.presentes}
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default MonthlyTrendChart;
